import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, parametresAPI } from '../api/services';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [adminMode, setAdminMode] = useState(() => {
    return localStorage.getItem('adminMode') || 'revendeur';
  });
  const [loading, setLoading] = useState(true);

  // Rafraîchir le user depuis /me
  const refreshUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) return;
      const res = await authAPI.me();
      const freshUser = res.data.data;
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch {
      // Token invalide → déconnexion
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
    }
  }, []);

  // Vérifier le token au chargement
  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !user) {
      refreshUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login(email, password);
    const { accessToken, refreshToken, user: userData } = res.data.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Ignorer les erreurs de logout
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const toggleAdminMode = useCallback(() => {
    const next = adminMode === 'platform' ? 'revendeur' : 'platform';
    localStorage.setItem('adminMode', next);
    window.location.href = '/';
  }, [adminMode]);

  const isAuthenticated = !!user;
  const canToggleAdmin = user?.role === 'admin';

  // Rafraîchir les permissions quand l'onglet revient au focus
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleFocus = () => refreshUser();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated, refreshUser]);

  // Vérifier une permission — admin et revendeur passent toujours
  const canDo = useCallback((permission) => {
    if (!user) return false;
    if (['admin', 'revendeur'].includes(user.role)) return true;
    return !!user.permissions?.[permission];
  }, [user]);

  // Auto-logout par inactivite
  const timerRef = useRef(null);
  const decoMinutesRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    parametresAPI.get()
      .then(res => {
        const p = res.data?.data || res.data;
        const minutes = parseInt(p?.security?.decoAuto) || 0;
        decoMinutesRef.current = minutes;
      })
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      const minutes = decoMinutesRef.current;
      if (minutes > 0) {
        timerRef.current = setTimeout(() => {
          logout();
          window.location.href = '/login';
        }, minutes * 60 * 1000);
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isAuthenticated, logout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated, canDo, adminMode, toggleAdminMode, canToggleAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return ctx;
};
